import { defineMessages } from 'react-intl.macro';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import './ResourceList.scss';

import { useMessage } from '@tmapy/intl';
import { ensureArray } from '@tmapy/utils';
import {
  SvgFile,
  SvgMap,
  SvgDatabase,
  CtaLink,
  SIZE,
  Link,
  LINK_SEVERITY,
  Icon,
} from '@tmapy/style-guide';

import { ResourceData } from '../types';
import { ResourceLinkList } from './ResourceLinkList';
import { RESOURCE_CATEGORY } from '../constants';

const getResourceIcon = (category: RESOURCE_CATEGORY) => {
  switch (category) {
    case RESOURCE_CATEGORY.DB_SCHEMA:
      return <SvgDatabase />;
    case RESOURCE_CATEGORY.MAP_SERVICE:
      return <SvgMap />;
    case RESOURCE_CATEGORY.OTHER:
    default:
      return <SvgFile />;
  }
};

const msg = defineMessages({
  capabilities: { id: 'resource.capabilities', defaultMessage: 'Získat' },
  openData: {
    id: 'resource.openData',
    defaultMessage: 'Otevřená data',
  },
});

type ResourceProps = {
  resources: ResourceData[];
};

export const ResourceList: React.FC<ResourceProps> = ({ resources }) => {
  const formatMessage = useMessage();
  return (
    <>
      {resources.map((resource, idx) => {
        const isCapabilitiesBtnEnabled = !!(resource.url && resource.access);
        return (
          <article className='sg-a-d-f sg-a-cg-2' key={idx}>
            <div className='sg-a-f-n sg-a-py-1'>
              <div className='e-resource--figure'>
                <Icon element={getResourceIcon(resource.category)} size={SIZE.MED} />
                {resource.type && <div className='e-resource--figureLabel'>{resource.type}</div>}
              </div>
            </div>
            <div className='sg-a-f-a sg-a-py-1 sg-u-bt'>
              <header className='sg-a-d-f sg-a-jc-sb sg-a-cg-2'>
                <h4 className='e-resource--title'>
                  {typeof resource.title === 'string' ? (
                    <span>{resource.title}</span>
                  ) : (
                    (resource.title as string[]).map((item) => <span key={item}>{item}</span>)
                  )}
                </h4>
                {isCapabilitiesBtnEnabled && resource.url && (
                  <div>
                    <CtaLink target='_blank' size={SIZE.SML} href={resource.url}>
                      <FormattedMessage {...msg.capabilities} values={{ type: resource.type }} />
                    </CtaLink>
                  </div>
                )}
              </header>
              {(resource.opendata_url || resource.licence_name) && (
                <div className='sg-a-mt-1/2 sg-a-d-f sg-a-fw-w sg-a-cg-1 sg-a-ai-c sg-u-type-xsm'>
                  {resource.opendata_url && (
                    <a
                      href={resource.opendata_url}
                      target='_blank'
                      title={formatMessage(msg.openData)}
                      rel='noreferrer'
                    >
                      <img
                        className='e-resource--openDataLogo'
                        src='./img/open-data.png'
                        alt={formatMessage(msg.openData)}
                      />
                    </a>
                  )}
                  {resource.licence_name && (
                    <Link
                      href={resource.licence_url!}
                      target='_blank'
                      severity={LINK_SEVERITY.COLORFUL}
                      tooltip={resource.licence_name}
                      isExternal
                    >
                      {`${resource.licence_name} licence`}
                    </Link>
                  )}
                </div>
              )}
              {resource.links.length > 0 && (
                <section className='sg-a-mt-2'>
                  {resource.links.map((resource, idx) => (
                    <ResourceLinkList
                      key={idx}
                      title={resource.title}
                      links={ensureArray(resource.values)}
                    />
                  ))}
                </section>
              )}
            </div>
          </article>
        );
      })}
    </>
  );
};
